import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import { Pastille } from "./badges";
import { CIBLE_GESTE, type Geste, type GesteDetail, type Pari } from "./contract";
import {
  ageEnClair,
  estAncien,
  formatDate,
  guidePerime,
  joursDepuis,
  relanceEnClair,
  typeEnClair,
  verificationEnClair,
} from "./format";
import { dossierIdee, GITHUB_BLOB, renderMarkdown } from "./markdown";
import { BoutonCocher } from "./portefeuille-cocher";
import { relectureDuGeste, useDemandes } from "./portefeuille-demandes";
import { LienPari } from "./portefeuille-liens";
import { useGesteHorsIdee, useGesteIdee, useRelance } from "./queries";
import { ConsoleShell } from "./shell";
import { Chargement, EcranDErreur, panneAffichee } from "./states";

/**
 * La page d'un guide : ce que le geste debloque, les etapes, comment verifier, comment
 * cocher. Trois portes d'entree pour un seul corps de page (spec des gestes guides,
 * section 4) : le prerequis d'une fiche, le geste hors idee, la relance d'une idee.
 *
 * Quatre choix qui expliquent la forme du fichier :
 *
 * - **Le bloc « Cocher » est sorti du fil du guide** et pose dans une colonne de droite
 *   sur grand ecran, pour rester sous la main pendant les etapes. Sur telephone la
 *   colonne retombe a sa place d'origine : apres « Verifier », avant « Si ca coince »,
 *   qui reste dans le fil a gauche sur grand ecran (maquettes `Guide` et `GuideMobile`).
 * - **Le bouton « C'est fait » se glisse entre le titre du bloc et son texte**, qui porte la
 *   commande de secours (`portefeuille-cocher.tsx`). D'ou le titre pose par la page et non
 *   par le fichier. Les deux pages d'un geste ont le bouton, jamais celle d'une relance :
 *   une relance se ferme en mettant la fiche a jour.
 * - **Le bouton Copier est pose par nous, jamais par le fichier.** Le markdown du depot
 *   ne peut pas fabriquer de bouton : la page enveloppe chaque bloc de code apres coup et
 *   ecoute les clics sur son propre conteneur, comme la page d'une idee le fait pour les
 *   liens d'onglets.
 * - **Les liens relatifs partent du dossier du guide**, pas de celui de l'idee : un guide
 *   vit un cran plus bas (`gestes/`), et `../00-fiche.md` doit tomber sur la fiche.
 */

/** Dossier des gestes hors idee dans le depot. */
const DOSSIER_GESTES = "docs/gestes";

/** Le titre du bloc qui sort du fil pour la colonne de droite. */
const TITRE_COCHER = /^##[ \t]+cocher[ \t]*$/i;

/** Un titre de niveau 2, quel qu'il soit : celui qui ferme le bloc « Cocher ». */
const TITRE_2 = /^##[ \t]/;

/** Ouverture ou fermeture d'un bloc clos, en debut de ligne : ``` ou ~~~. */
const CLOTURE = /^[ \t]*(```|~~~)/;

/**
 * Coupe un guide autour de son bloc « Cocher » : ce qui le precede, le bloc lui-meme (son
 * titre compris), et ce qui le suit (« Si ca coince », dernier bloc du squelette). Un guide
 * qui n'en a pas (ce que la CI refuse, mais le serveur peut etre en retard d'un deploiement)
 * reste entier.
 *
 * La recherche saute les blocs clos : `docs/gestes/modeles/relance.md` est affiche tel
 * quel comme guide, et un modele montre le squelette dans un bloc de code. Couper sur ce
 * `## Cocher`-la couperait la page au milieu d'un exemple.
 */
export function couperCocher(markdown: string): {
  avant: string;
  cocher: string | null;
  apres: string;
} {
  const lignes = markdown.split("\n");
  let dansUnBloc = false;
  let position = 0;
  let debut: number | null = null;
  for (const ligne of lignes) {
    if (CLOTURE.test(ligne)) dansUnBloc = !dansUnBloc;
    else if (!dansUnBloc && debut === null && TITRE_COCHER.test(ligne)) debut = position;
    else if (!dansUnBloc && debut !== null && TITRE_2.test(ligne)) {
      return {
        avant: markdown.slice(0, debut).trimEnd(),
        cocher: markdown.slice(debut, position).trimEnd(),
        apres: markdown.slice(position),
      };
    }
    position += ligne.length + 1;
  }
  if (debut === null) return { avant: markdown, cocher: null, apres: "" };
  return { avant: markdown.slice(0, debut).trimEnd(), cocher: markdown.slice(debut), apres: "" };
}

/** Le bloc « Cocher » sans sa ligne de titre, que la page pose elle-meme. */
function sansTitre(cocher: string): string {
  const fin = cocher.indexOf("\n");
  return fin === -1 ? "" : cocher.slice(fin + 1);
}

/**
 * Vrai quand le bloc « Cocher » du guide porte deja la commande de secours de cette cible : la
 * carte ne la montre alors qu'une fois, celle du guide, juste sous le bouton. La CI l'exige de
 * tout guide ouvert, mais un serveur en retard d'un deploiement peut encore servir un guide
 * qui ne l'a pas : le bouton donne alors la sienne. La cible doit finir la ou finit le mot, le
 * guide de `gestes/jeton-long` ne vaut pas pour `gestes/jeton`.
 */
export function guidePorteLeSecours(markdown: string, cible: string | null): boolean {
  // `CIBLE_GESTE` ne laisse passer que des minuscules, des chiffres, des tirets et une barre :
  // rien qui compte dans une expression reguliere.
  if (cible === null || !CIBLE_GESTE.test(cible)) return false;
  const { cocher } = couperCocher(markdown);
  return cocher !== null && new RegExp(`geste-fait\\.ps1[ \\t]+${cible}(?![a-z0-9-])`).test(cocher);
}

/**
 * Les boutons Copier que cette page a fabriques, et les blocs deja traites.
 *
 * C'est le garde-fou du presse-papiers, et il vaut d'etre explique. Le HTML d'un guide
 * vient d'un fichier du depot : DOMPurify en retire les scripts, mais pas tout le HTML.
 * Un fichier pourrait donc ecrire son propre bouton, ou une classe `bloc-code` qui ferait
 * sauter le tour de celui-ci. Une marque en attribut se recopie ; **une reference dans un
 * WeakSet, non** : un element venu du fichier n'y sera jamais, quoi qu'il porte.
 */
const NOS_BOUTONS = new WeakSet<HTMLButtonElement>();
const BLOCS_TRAITES = new WeakSet<HTMLElement>();

/** Etat d'un bouton : le texte visible, et le nom lu a voix haute. */
function poserEtat(bouton: HTMLButtonElement, texte: string, quoi: string): void {
  bouton.textContent = texte;
  bouton.setAttribute("aria-label", `${texte} : ${quoi}`);
}

/**
 * Le titre de niveau 2 qui precede un bloc, pour nommer son bouton. Plusieurs « Copier »
 * identiques sur une page ne se distinguent ni au lecteur d'ecran ni au clavier.
 *
 * C'est l'ordre du document qui compte, pas la fratrie : le titre « Cocher » est pose par la
 * page, et le texte du bloc suit dans son propre conteneur, apres le bouton « C'est fait ».
 * Un bloc de code dans une etape numerotee prend de meme le titre « Etapes ».
 */
function quoiCopier(bloc: Element, conteneur: Element): string {
  let titre: Element | null = null;
  for (const candidat of conteneur.querySelectorAll("h2")) {
    if (!(candidat.compareDocumentPosition(bloc) & Node.DOCUMENT_POSITION_FOLLOWING)) break;
    titre = candidat;
  }
  return titre === null ? "bloc de code" : `bloc de code de « ${titre.textContent?.trim() ?? ""} »`;
}

/**
 * Pose un bouton Copier sur chaque bloc de code du conteneur, et un seul ecouteur de clic
 * dessus. Trois invariants, tenus ici et nulle part ailleurs :
 *
 * - **ce qui est copie est ce qui est affiche.** La copie prend le `textContent` du `pre`
 *   entier, celui que l'oeil lit, jamais celui d'un enfant qui pourrait en cacher ou en
 *   omettre une partie. Aucun test ne distingue cette garde de la suivante (avec un seul
 *   noeud enfant, les deux textes sont egaux) : elle est la pour que l'egalite reste vraie
 *   le jour ou la suivante bougera, pas parce qu'elle attrape un cas d'aujourd'hui ;
 * - **seul un bloc clos du markdown recoit un bouton.** `marked` rend une cloture en
 *   `<pre><code>texte</code></pre>` : un seul noeud enfant, et rien d'autre dans le `code`.
 *   Compter les **noeuds** et pas seulement les elements est ce qui ferme le cas
 *   `<pre><code hidden>charge</code>leurre visible</pre>`, ou le texte frere du `code`
 *   n'est compte par personne ;
 * - **seul un bouton fabrique ici declenche la copie** (voir `NOS_BOUTONS`). Cette
 *   troisieme garde n'a pas de test a elle : le tamis de `markdown.ts` empeche deja un
 *   fichier de fabriquer un bouton. Elle reste parce qu'elle ne depend pas de lui.
 */
function useBoutonsCopier(conteneur: React.RefObject<HTMLElement | null>, html: string): void {
  useEffect(() => {
    const element = conteneur.current;
    // `html` est la condition autant que la matiere : c'est lui qui remplace le contenu du
    // conteneur, donc les blocs a envelopper, et il n'y a rien a faire tant qu'il est vide.
    if (element === null || html === "") return;
    for (const bloc of element.querySelectorAll("pre")) {
      if (BLOCS_TRAITES.has(bloc)) continue;
      const code = bloc.firstElementChild;
      const clos =
        bloc.childNodes.length === 1 &&
        code instanceof HTMLElement &&
        code.tagName === "CODE" &&
        code.childNodes.length <= 1 &&
        code.childElementCount === 0;
      if (!clos) continue;
      BLOCS_TRAITES.add(bloc);
      const enveloppe = document.createElement("div");
      enveloppe.className = "bloc-code";
      bloc.replaceWith(enveloppe);
      enveloppe.append(bloc);
      const bouton = document.createElement("button");
      bouton.type = "button";
      bouton.className = "copier";
      // Le bouton s'annonce lui-meme : son nom change quand la copie aboutit ou echoue.
      bouton.setAttribute("aria-live", "polite");
      poserEtat(bouton, "Copier", quoiCopier(enveloppe, element));
      NOS_BOUTONS.add(bouton);
      enveloppe.append(bouton);
    }
  }, [conteneur, html]);

  useEffect(() => {
    const element = conteneur.current;
    if (element === null) return;
    const minuteries = new Map<HTMLButtonElement, ReturnType<typeof setTimeout>>();
    const dire = (bouton: HTMLButtonElement, texte: string): void => {
      const quoi = quoiCopier(bouton.parentElement ?? bouton, element);
      poserEtat(bouton, texte, quoi);
      const ancienne = minuteries.get(bouton);
      if (ancienne !== undefined) clearTimeout(ancienne);
      // Le bouton revient au repos : un « Copie impossible » fige laisserait croire qu'il
      // ne marchera plus jamais, alors qu'une permission peut etre accordee entre-temps.
      minuteries.set(
        bouton,
        setTimeout(() => poserEtat(bouton, "Copier", quoi), 4000),
      );
    };
    const copier = (event: MouseEvent): void => {
      const cible = event.target instanceof Element ? event.target.closest("button") : null;
      if (!(cible instanceof HTMLButtonElement) || !NOS_BOUTONS.has(cible)) return;
      // Le `pre` entier, pas son `code` : « copie » et « affiche » sont alors le meme texte
      // par construction, et non par confiance dans ce qu'il y a dedans.
      const texte = cible.parentElement?.querySelector("pre")?.textContent ?? "";
      if (texte === "") return;
      // Le presse-papiers n'existe pas partout (contexte non securise, navigateur ancien,
      // refus de l'utilisateur) : le bouton le dit plutot que de faire semblant.
      const promesse = navigator.clipboard?.writeText(texte);
      if (promesse === undefined) {
        dire(cible, "Copie impossible");
        return;
      }
      promesse.then(
        () => dire(cible, "Copié"),
        () => dire(cible, "Copie impossible"),
      );
    };
    element.addEventListener("click", copier);
    return () => {
      element.removeEventListener("click", copier);
      for (const minuterie of minuteries.values()) clearTimeout(minuterie);
    };
  }, [conteneur]);
}

/** Le fil d'Ariane, du portefeuille jusqu'a la page courante. */
function Fil({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm text-muted-foreground">
      <Link className="underline underline-offset-2" to="/">
        Portefeuille
      </Link>{" "}
      {children}
    </p>
  );
}

/** La ligne de confiance : depuis quand le guide n'a pas ete relu. */
function Verification({ verifieLe, maintenant }: { verifieLe: string | null; maintenant: number }) {
  return (
    <p
      className={cn(
        "text-sm",
        guidePerime(verifieLe, maintenant) || verifieLe === null
          ? "font-medium text-destructive"
          : "text-muted-foreground",
      )}
    >
      {verificationEnClair(verifieLe, maintenant)}
    </p>
  );
}

/**
 * Le guide rendu : les blocs dans le fil, « Cocher » a droite sur grand ecran avec le bouton
 * que la page lui donne. Le HTML vient de `renderMarkdown` (marked, DOMPurify, liens
 * reecrits) et de nulle part ailleurs.
 *
 * Sur grand ecran, « Si ca coince » passe sous le guide, dans la colonne de gauche, et la
 * colonne de droite couvre les deux rangees. L'ordre du document reste celui du fichier : le
 * telephone, le clavier et le lecteur d'ecran le suivent tel quel.
 */
function CorpsDuGuide({
  markdown,
  dossier,
  lienFichier,
  bouton,
}: {
  markdown: string;
  dossier: string;
  lienFichier: string;
  /** Le bouton « C'est fait » : absent sur la page d'une relance. */
  bouton?: ReactNode;
}) {
  const { avant, cocher, apres } = useMemo(() => couperCocher(markdown), [markdown]);
  const contexte = useMemo(() => ({ dossier, fichiers: [] as string[] }), [dossier]);
  const htmlGuide = useMemo(() => renderMarkdown(avant, contexte), [avant, contexte]);
  const htmlCocher = useMemo(
    () => (cocher === null ? "" : renderMarkdown(sansTitre(cocher), contexte)),
    [cocher, contexte],
  );
  const htmlApres = useMemo(() => renderMarkdown(apres, contexte), [apres, contexte]);
  const conteneur = useRef<HTMLDivElement>(null);
  useBoutonsCopier(conteneur, `${htmlGuide}${htmlCocher}${htmlApres}`);

  return (
    <div
      className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_20rem] md:grid-rows-[auto_1fr]"
      ref={conteneur}
    >
      <Card className="md:col-start-1 md:row-start-1">
        <article
          aria-label="Guide"
          className="markdown flex flex-col gap-3 p-4 text-sm"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: markdown du depot, deja passe par DOMPurify dans renderMarkdown
          dangerouslySetInnerHTML={{ __html: htmlGuide }}
        />
      </Card>
      <div className="flex flex-col gap-3 md:col-start-2 md:row-span-2 md:row-start-1">
        {cocher === null && bouton === undefined ? null : (
          <Card>
            <aside aria-label="Cocher" className="flex flex-col gap-3 p-4 text-sm">
              <h2 className="text-lg font-semibold leading-snug">Cocher</h2>
              {bouton}
              {htmlCocher === "" ? null : (
                <div
                  className={cn(
                    "markdown flex flex-col gap-3",
                    bouton !== undefined && "border-t border-border pt-3",
                  )}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: markdown du depot, deja passe par DOMPurify dans renderMarkdown
                  dangerouslySetInnerHTML={{ __html: htmlCocher }}
                />
              )}
            </aside>
          </Card>
        )}
        <p className="text-xs text-muted-foreground">
          <a
            className="underline underline-offset-2"
            href={`${GITHUB_BLOB}${lienFichier}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            le guide dans le dépôt
          </a>
        </p>
      </div>
      {htmlApres === "" ? null : (
        <Card className="md:col-start-1 md:row-start-2">
          <div
            className="markdown flex flex-col gap-3 p-4 text-sm"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: markdown du depot, deja passe par DOMPurify dans renderMarkdown
            dangerouslySetInnerHTML={{ __html: htmlApres }}
          />
        </Card>
      )}
    </div>
  );
}

/** L'en-tete d'un geste : ce qu'il faut faire, son type, son age, ce qu'il debloque. */
function EnTeteGeste({ geste, maintenant }: { geste: Geste; maintenant: number }) {
  return (
    <header className="flex flex-col gap-2">
      <h1 className="text-2xl font-semibold">{geste.quoi}</h1>
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <Pastille>{typeEnClair(geste.type)}</Pastille>
        <span
          className={
            !geste.fait && estAncien(geste.depuis, maintenant)
              ? "font-medium text-destructive"
              : undefined
          }
        >
          {geste.fait ? "déjà fait" : ageEnClair(geste.depuis, maintenant)}
        </span>
        <span>
          débloque :{" "}
          {geste.pari !== null && geste.titre !== null ? (
            <LienPari court discret id={geste.pari} titre={geste.titre} />
          ) : (
            "hors idée"
          )}
        </span>
        {geste.issue === null ? null : (
          <a
            className="underline underline-offset-2"
            href={`https://github.com/kavaliero/ai-revenue-lab/issues/${geste.issue}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            issue #{geste.issue}
          </a>
        )}
      </p>
    </header>
  );
}

/**
 * Le bouton d'un geste. Il sait si le bloc du guide, juste en dessous, porte deja la commande
 * de secours : une carte n'en montre jamais deux.
 */
function boutonDuGeste({ geste, guide }: GesteDetail): ReactNode {
  return (
    <BoutonCocher
      geste={geste}
      key={geste.cible}
      secoursDansLeGuide={guidePorteLeSecours(guide.markdown, geste.cible)}
    />
  );
}

/** Le guide d'un prerequis de fiche : `/portefeuille/idees/$slug/gestes/$nom`. */
export function GuideIdeePage({
  slug,
  nom,
  maintenant = Date.now(),
}: {
  slug: string;
  nom: string;
  maintenant?: number;
}) {
  // Tant qu'une demande « C'est fait » attend son commit, le guide se relit : c'est ainsi que
  // le geste passe « fait » sous les yeux de Nicolas.
  const demandes = useDemandes(maintenant);
  const lecture = useGesteIdee(slug, nom, (lu) => relectureDuGeste(lu, demandes, Date.now()));
  const dossier = `${dossierIdee(slug)}/gestes`;

  return (
    <ConsoleShell connecte={lecture.isSuccess}>
      <Fil>
        /{" "}
        <Link
          className="underline underline-offset-2"
          params={{ slug }}
          to="/portefeuille/idees/$slug"
        >
          {slug}
        </Link>{" "}
        / {nom}
      </Fil>
      {lecture.isPending ? <Chargement quoi="du guide" /> : null}
      {panneAffichee(lecture) ? (
        <EcranDErreur error={lecture.error} quoi={`le guide ${slug}/${nom}`} />
      ) : null}
      {lecture.data && !panneAffichee(lecture) ? (
        <>
          <EnTeteGeste geste={lecture.data.geste} maintenant={maintenant} />
          <Verification maintenant={maintenant} verifieLe={lecture.data.guide.verifieLe} />
          <CorpsDuGuide
            bouton={boutonDuGeste(lecture.data)}
            dossier={dossier}
            lienFichier={`${dossier}/${nom}.md`}
            markdown={lecture.data.guide.markdown}
          />
        </>
      ) : null}
    </ConsoleShell>
  );
}

/** Le guide d'un geste hors idee : `/portefeuille/gestes/$nom`. */
export function GuideHorsIdeePage({
  nom,
  maintenant = Date.now(),
}: {
  nom: string;
  maintenant?: number;
}) {
  const demandes = useDemandes(maintenant);
  const lecture = useGesteHorsIdee(nom, (lu) => relectureDuGeste(lu, demandes, Date.now()));

  return (
    <ConsoleShell connecte={lecture.isSuccess}>
      <Fil>/ gestes / {nom}</Fil>
      {lecture.isPending ? <Chargement quoi="du guide" /> : null}
      {panneAffichee(lecture) ? (
        <EcranDErreur error={lecture.error} quoi={`le geste ${nom}`} />
      ) : null}
      {lecture.data && !panneAffichee(lecture) ? (
        <>
          <EnTeteGeste geste={lecture.data.geste} maintenant={maintenant} />
          <Verification maintenant={maintenant} verifieLe={lecture.data.guide.verifieLe} />
          <CorpsDuGuide
            bouton={boutonDuGeste(lecture.data)}
            dossier={DOSSIER_GESTES}
            lienFichier={`${DOSSIER_GESTES}/${nom}.md`}
            markdown={lecture.data.guide.markdown}
          />
        </>
      ) : null}
    </ConsoleShell>
  );
}

/** Ce qui decide d'une relance : le declencheur, le pourquoi, la date. */
function EnTeteRelance({ pari, maintenant }: { pari: Pari; maintenant: number }) {
  // `joursDepuis` compte a l'envers d'une relance : positif quand la date est passee.
  const ecoules = joursDepuis(pari.prochaineDate, maintenant);
  const jours = ecoules === null ? null : -ecoules;

  return (
    <>
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Relance : {pari.titre}</h1>
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <Pastille>relance</Pastille>
          <span
            className={jours !== null && jours < 0 ? "font-medium text-destructive" : undefined}
          >
            {pari.prochaineDate ? formatDate(pari.prochaineDate) : "sans date"}
            {jours === null ? "" : ` (${relanceEnClair(jours)})`}
          </span>
          <span>
            pari : <LienPari court discret id={pari.id} titre={pari.titre} />
          </span>
        </p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle>Ce qui rouvrirait le dossier</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <p>
            <span className="font-semibold">Déclencheur : </span>
            {pari.declencheur ?? "aucun"}
          </p>
          <p>
            <span className="font-semibold">Pourquoi : </span>
            {pari.pourquoi ?? "non renseigné"}
          </p>
        </CardContent>
      </Card>
    </>
  );
}

/**
 * La relance d'une idee : `/portefeuille/idees/$slug/relance`. Pas de bouton ici, jamais :
 * une relance se ferme en mettant a jour la fiche (nouvelle date, ou statut change), pas
 * en cochant quoi que ce soit.
 */
export function RelancePage({
  slug,
  maintenant = Date.now(),
}: {
  slug: string;
  maintenant?: number;
}) {
  const lecture = useRelance(slug);
  // Le guide generique vit dans `modeles/`, celui d'une idee dans son propre dossier :
  // les liens relatifs ne partent pas du meme endroit.
  const dossier = lecture.data?.guide.surcharge
    ? `${dossierIdee(slug)}/gestes`
    : `${DOSSIER_GESTES}/modeles`;

  return (
    <ConsoleShell connecte={lecture.isSuccess}>
      <Fil>
        /{" "}
        <Link
          className="underline underline-offset-2"
          params={{ slug }}
          to="/portefeuille/idees/$slug"
        >
          {slug}
        </Link>{" "}
        / relance
      </Fil>
      {lecture.isPending ? <Chargement quoi="de la relance" /> : null}
      {lecture.isError ? (
        <EcranDErreur error={lecture.error} quoi={`la relance de ${slug}`} />
      ) : null}
      {lecture.data ? (
        <>
          <EnTeteRelance maintenant={maintenant} pari={lecture.data.pari} />
          <Verification maintenant={maintenant} verifieLe={lecture.data.guide.verifieLe} />
          <CorpsDuGuide
            dossier={dossier}
            lienFichier={`${dossier}/relance.md`}
            markdown={lecture.data.guide.markdown}
          />
        </>
      ) : null}
    </ConsoleShell>
  );
}
